import type { IconName } from '@whinerp/ui';
import type { EntitlementKey } from '@whinerp/contracts/permissions';
import type { ModuleCode, ScreenId } from '@whinerp/contracts/generated/spec';

/**
 * The navigation model.
 *
 * UX-01 caps the rail at seven items, which the first version read as "show
 * seven and hide the rest". That is the product's surface being capped, not
 * the tenant's: a workspace with four modules on saw four items and an
 * overflow that never filled, while one with eleven lost four of them behind
 * an index on Home.
 *
 * Grouping is what the rule actually asks for. Seven GROUPS, each rendered
 * only when something inside it is entitled, so a Core-only workspace sees
 * three and an everything workspace sees all of them - and nothing is ever
 * hidden from the person who is paying for it.
 */

/** The groups, in the order they are drawn. `null` means no header. */
export const NAV_GROUPS = [
  { key: 'dashboard', label: 'nav.group.dashboard' },
  { key: 'crm', label: 'nav.group.crm' },
  { key: 'sales', label: 'nav.group.sales' },
  { key: 'marketing', label: 'nav.group.marketing' },
  { key: 'service', label: 'nav.group.service' },
  { key: 'agents', label: 'nav.group.agents' },
  { key: 'insight', label: 'nav.group.insight' },
  { key: 'admin', label: 'nav.group.admin' },
] as const;

export type NavGroupKey = (typeof NAV_GROUPS)[number]['key'];
export interface NavArea {
  key: string;
  label: string;
  /** lucide-react icon name. One icon set; the size comes from tokens.icon.default. */
  icon: IconName;
  route: string;
  /** The screen a person lands on. */
  screen: ScreenId;
  module: ModuleCode;
  /** Hidden entirely when this entitlement is off. */
  entitlement: EntitlementKey | 'always-on';
  /** Lower sorts first when the tenant has expressed no preference. */
  defaultOrder: number;
  /** One line explaining the area, shown on the Home index. */
  blurb: string;
  /** Which group it is drawn under. */
  group: NavGroupKey;
  /**
   * Present when the area is named but not built.
   *
   * The item is drawn and disabled rather than omitted: somebody who bought a
   * plan that lists it should see where it will be, and a workspace that
   * cannot find it at all files a support ticket asking whether it exists.
   * The value is the feature that will fill it, which is what the tripwire in
   * check:tripwires watches.
   */
  notBuilt?: string;
}

export const NAV_AREAS: readonly NavArea[] = [
  /*
   * Every screen a person can open without already holding an id.
   *
   * A nav item is a link, so a screen whose route carries a parameter cannot
   * be one: `/contacts/:id` needs a contact. Those are reached from the list
   * that owns them, which is what `reachedBy` records and what
   * check:features walks. The areas below are the parameterless routes, plus
   * the one well-known id this product already uses - `/dashboards/home`.
   *
   * The entitlement on each is the ENTITLEMENT OF THE SCREEN IT OPENS, not
   * of the module around it. A module can be on while the feature behind one
   * of its screens is off, and an item that leads somewhere the workspace
   * cannot open is worse than an absent one.
   */
  {
    key: 'home',
    label: 'Home',
    icon: 'House',
    route: '/',
    screen: 'SC-04',
    module: 'M01',
    entitlement: 'always-on',
    defaultOrder: 10,
    blurb: 'What needs doing today.',
    group: 'dashboard',
  },
  {
    key: 'work',
    label: 'My work',
    icon: 'CheckSquare',
    route: '/work',
    screen: 'SC-38',
    module: 'M02',
    entitlement: 'F-155',
    defaultOrder: 20,
    blurb: 'Every scheduled next step, across every record.',
    group: 'dashboard',
  },
  {
    key: 'approvals',
    label: 'Approvals',
    icon: 'SealCheck',
    route: '/approvals',
    screen: 'SC-68',
    module: 'M02',
    entitlement: 'F-090',
    defaultOrder: 25,
    blurb: 'What is waiting on your approval, and who each chain is waiting on.',
    group: 'dashboard',
  },
  {
    /*
     * Named, and not built.
     *
     * F-086 is blocked on inbound email, so there is no route and no screen.
     * It is drawn disabled rather than left out: a plan that lists a shared
     * inbox and a product that shows no trace of one produces a support
     * ticket asking whether it exists. `check:tripwires` fails the day a
     * route for it appears, so the nav item is enabled in the same change
     * rather than a fortnight later.
     */
    key: 'inbox',
    label: 'Inbox',
    icon: 'Tray',
    route: '/inbox',
    screen: 'SC-04',
    module: 'M02',
    entitlement: 'F-086',
    defaultOrder: 30,
    blurb: 'Email and messages that arrived for the whole team.',
    group: 'dashboard',
    notBuilt: 'F-086',
  },

  {
    key: 'contacts',
    label: 'Contacts',
    icon: 'Users',
    route: '/contacts',
    screen: 'SC-06',
    module: 'M02',
    entitlement: 'F-027',
    defaultOrder: 40,
    blurb: 'Everybody you know, and what was said last.',
    group: 'crm',
  },
  {
    /*
     * Companies, which had a record page and no list.
     *
     * `listAccounts` was published, paginated and agent-callable the whole
     * time with no caller in the product: the only route to a company was
     * through a contact who happened to work there.
     */
    key: 'companies',
    label: 'Companies',
    icon: 'Buildings',
    route: '/accounts',
    screen: 'SC-49',
    module: 'M02',
    entitlement: 'F-028',
    defaultOrder: 45,
    blurb: 'The organisations you sell to.',
    group: 'crm',
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: 'UserPlus',
    route: '/leads',
    screen: 'SC-09',
    module: 'M02',
    entitlement: 'F-036',
    defaultOrder: 50,
    blurb: 'People who have shown interest and are not yet qualified.',
    group: 'crm',
  },

  {
    key: 'deals',
    label: 'Deals',
    icon: 'Target',
    route: '/deals',
    screen: 'SC-12',
    module: 'M03',
    entitlement: 'F-040',
    defaultOrder: 60,
    blurb: 'The pipeline, by stage.',
    group: 'sales',
  },
  {
    key: 'cadences',
    label: 'Cadences',
    icon: 'Path',
    route: '/cadences',
    screen: 'SC-73',
    module: 'M03',
    entitlement: 'F-055',
    defaultOrder: 175,
    blurb: 'Outreach sequences that stop when somebody replies.',
    group: 'sales',
  },
  {
    key: 'promotions',
    label: 'Promotions',
    icon: 'Tag',
    route: '/promotions',
    screen: 'SC-47',
    module: 'M03',
    entitlement: 'F-163',
    defaultOrder: 70,
    blurb: 'Coupons, automatic discounts and what they have been used on.',
    group: 'sales',
  },
  {
    key: 'forecast',
    label: 'Forecast',
    icon: 'TrendUp',
    route: '/forecast',
    screen: 'SC-15',
    module: 'M03',
    entitlement: 'F-052',
    defaultOrder: 75,
    blurb: 'What is expected to close, beside what has.',
    group: 'sales',
  },
  {
    key: 'revenue',
    label: 'Recurring revenue',
    icon: 'ArrowsClockwise',
    route: '/revenue',
    screen: 'SC-45',
    module: 'M03',
    entitlement: 'F-164',
    defaultOrder: 80,
    blurb: 'Subscriptions, and what they are worth each month.',
    group: 'sales',
  },

  {
    key: 'campaigns',
    label: 'Campaigns',
    icon: 'Megaphone',
    route: '/campaigns',
    screen: 'SC-16',
    module: 'M04',
    entitlement: 'F-062',
    defaultOrder: 90,
    blurb: 'What you have sent, and what it did.',
    group: 'marketing',
  },
  {
    /*
     * Audiences, which had a builder and no list.
     *
     * A saved segment was reachable only by typing its URL, and until F-063
     * nothing could save one at all. A record type with no list is a record
     * type nobody can find.
     */
    key: 'segments',
    label: 'Audiences',
    icon: 'UsersThree',
    route: '/segments',
    screen: 'SC-50',
    module: 'M04',
    entitlement: 'F-063',
    defaultOrder: 95,
    blurb: 'Saved audiences, and how many people are in each.',
    group: 'marketing',
  },  {
    key: 'journeys',
    label: 'Journeys',
    icon: 'FlowArrow',
    route: '/journeys',
    screen: 'SC-51',
    module: 'M04',
    entitlement: 'F-068',
    defaultOrder: 96,
    blurb: 'Sequences that take somebody through several steps over days.',
    group: 'marketing',
  },
  {
    key: 'automations',
    label: 'Automations',
    icon: 'Lightning',
    route: '/automations',
    screen: 'SC-52',
    module: 'M07',
    entitlement: 'F-068',
    defaultOrder: 97,
    blurb: 'Things that happen on their own when something else does.',
    group: 'marketing',
  },


  {
    key: 'support',
    label: 'Cases',
    icon: 'Lifebuoy',
    route: '/cases',
    screen: 'SC-28',
    module: 'M05',
    entitlement: 'F-085',
    defaultOrder: 100,
    blurb: 'Everything a customer is waiting on.',
    group: 'service',
  },
  {
    key: 'chat',
    label: 'Live chat',
    icon: 'ChatCircle',
    route: '/chat',
    screen: 'SC-39',
    module: 'M05',
    entitlement: 'F-151',
    defaultOrder: 110,
    blurb: 'Conversations happening on the website right now.',
    group: 'service',
  },

  {
    /*
     * The agents themselves. The key `agents` was already taken by the
     * proposal queue at /agents/proposals, so the list of agents had no way in
     * from the sidebar at all.
     */
    key: 'agentsList',
    label: 'Agents',
    icon: 'Sparkle',
    route: '/agents',
    screen: 'SC-54',
    module: 'M08',
    entitlement: 'F-105',
    defaultOrder: 118,
    blurb: 'Every agent, and what each is allowed to do.',
    group: 'agents',
  },
  {
    key: 'agents',
    label: 'Waiting for you',
    icon: 'Sparkle',
    route: '/agents/proposals',
    screen: 'SC-27',
    module: 'M08',
    entitlement: 'F-111',
    defaultOrder: 120,
    blurb: 'What the agents propose to do, for a person to decide.',
    group: 'agents',
  },
  {
    key: 'agentActivity',
    label: 'Agent activity',
    icon: 'Pulse',
    route: '/agents/activity',
    screen: 'SC-26',
    module: 'M08',
    entitlement: 'F-112',
    defaultOrder: 130,
    blurb: 'Every run an agent has made, and what it cost.',
    group: 'agents',
  },

  {
    key: 'reports',
    label: 'Dashboards',
    icon: 'SquaresFour',
    route: '/dashboards/home',
    screen: 'SC-24',
    module: 'M06',
    entitlement: 'F-098',
    defaultOrder: 140,
    blurb: 'The figures, arranged the way you read them.',
    group: 'insight',
  },

  {
    key: 'reportsList',
    label: 'Reports',
    icon: 'ChartBar',
    route: '/reports',
    screen: 'SC-53',
    module: 'M06',
    entitlement: 'F-097',
    defaultOrder: 145,
    blurb: 'Saved reports, and who can see each.',
    group: 'insight',
  },
  {
    key: 'products',
    label: 'Products',
    icon: 'Package',
    route: '/products',
    screen: 'SC-55',
    module: 'M06',
    entitlement: 'F-045',
    defaultOrder: 75,
    blurb: 'The catalogue a quote is built from.',
    group: 'sales',
  },
  {
    key: 'pricelists',
    label: 'Pricelists',
    icon: 'Tag',
    route: '/pricelists',
    screen: 'SC-56',
    module: 'M06',
    entitlement: 'F-162',
    defaultOrder: 76,
    blurb: 'What a particular customer pays, by rules rather than by hand.',
    group: 'sales',
  },
  {
    key: 'setup',
    label: 'Setup',
    icon: 'Gear',
    route: '/setup',
    screen: 'SC-30',
    module: 'M01',
    entitlement: 'always-on',
    defaultOrder: 150,
    blurb: 'Objects, fields, layouts, people and permissions.',
    group: 'admin',
  },
  {
    key: 'modules',
    label: 'Modules',
    icon: 'Stack',
    route: '/setup/modules',
    screen: 'SC-31',
    module: 'M01',
    entitlement: 'always-on',
    defaultOrder: 160,
    blurb: 'What this workspace has switched on.',
    group: 'admin',
  },
  {
    key: 'import',
    label: 'Import',
    icon: 'Upload',
    route: '/setup/import',
    screen: 'SC-34',
    module: 'M01',
    entitlement: 'F-016',
    defaultOrder: 170,
    blurb: 'Bring records in from a spreadsheet, reversibly.',
    group: 'admin',
  },
  {
    key: 'plan',
    label: 'Plan and usage',
    icon: 'CreditCard',
    route: '/setup/plan',
    screen: 'SC-35',
    module: 'M11',
    entitlement: 'always-on',
    defaultOrder: 180,
    blurb: 'What you are on, and what it includes.',
    group: 'admin',
  },
];

export const NAV_RAIL_LIMIT = 7;

export interface NavGroup {
  key: NavGroupKey;
  /** A message key, or null for a group that carries no header. */
  label: string | null;
  items: NavArea[];
}

export interface NavigationModel {
  /** The groups with something in them, in order. Empty groups are not drawn. */
  groups: NavGroup[];
  /** Every enabled area, flat. */
  all: NavArea[];
}

export interface NavigationInput {
  /** Entitlement keys currently on for this workspace. */
  enabled: ReadonlySet<string>;
  /** Tenant-configured order, by area key. Unlisted areas keep their default order. */
  order?: readonly string[];
}

export function buildNavigation({ enabled, order = [] }: NavigationInput): NavigationModel {
  const rank = new Map(order.map((key, index) => [key, index]));
  const all = NAV_AREAS.filter(
    (area) => area.entitlement === 'always-on' || enabled.has(area.entitlement),
  ).sort((a, b) => {
    const ra = rank.get(a.key);
    const rb = rank.get(b.key);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.defaultOrder - b.defaultOrder;
  });

  const groups = NAV_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    items: all.filter((area) => area.group === group.key),
  }))
    // A group with nothing entitled in it is not drawn. A header standing over
    // no items tells a person the product has an area they cannot reach and
    // does not say why, which is worse than saying nothing.
    .filter((group) => group.items.length > 0);

  return { groups, all };
}
